import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Drawer from 'antd/lib/drawer';
import List from 'antd/lib/list';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Flex from 'antd/lib/flex';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import Tabs from 'antd/lib/tabs';
import { PullRequestOutlined } from '@ant-design/icons';
import { CommitItem, FeatureItem, FeatureItemCodeStatus, PRItem, PullRequest, mapCodeStatus } from '../../types';
import { RootState } from '../../reducers/reducers';
import { selectAllReleaseBranchWorkItems } from '../../selectors/selectors';
import { displayTextForPullRequestType } from '../../helpers/displayHelpers';
import { tagColorForFeatureItemCodeStatus } from '../../helpers';
import { antGrey, rwColors, tagStyle } from '../styles/globalStyles';
import CodeStatus from './CodeStatus';
import {
    CHERRY_PICK_QUEUE_STATUSES,
    isCommit,
    isInCherryPickQueue,
    isOpenBackmergePR,
} from './featureReadinessHelpers';

interface CherryPickQueueDrawerProps {
    appId: string;
    stepRunId: string;
    open: boolean;
    onClose: () => void;
    highlightedItem: FeatureItem | null;
    openBackmergePRs?: PullRequest[];
    isAutoMergeRunwayPRsEnabled: boolean;
}

type QueueItem = FeatureItem & { code: PRItem | CommitItem };

const codeDate = (code: PRItem | CommitItem) =>
    (isCommit(code) ? code.committedAt : (code.updatedAt ?? code.createdAt)) ?? '';

const codeTitle = (code: PRItem | CommitItem) => (isCommit(code) ? code.message : code.title);

const matchesSearch = (search: string, ...fields: (string | number | undefined)[]) =>
    fields.some((field) =>
        String(field ?? '')
            .toLowerCase()
            .includes(search),
    );

const queueCardStyle = (isHighlighted: boolean): React.CSSProperties => ({
    padding: 12,
    marginBottom: 8,
    borderRadius: 8,
    border: isHighlighted ? `2px solid ${rwColors.primary}` : `1px solid ${antGrey.grey5}`,
});

const byQueuePosition = (a: QueueItem, b: QueueItem) =>
    CHERRY_PICK_QUEUE_STATUSES.indexOf(mapCodeStatus(a.code.status)) -
        CHERRY_PICK_QUEUE_STATUSES.indexOf(mapCodeStatus(b.code.status)) ||
    codeDate(a.code).localeCompare(codeDate(b.code));

const selectCherryPickQueueItems = (state: RootState, stepRunId: string) =>
    selectAllReleaseBranchWorkItems(state, stepRunId).filter(
        (item): item is QueueItem => !item.ignored && isInCherryPickQueue(item.code),
    );

type QueueTab = 'cherryPicks' | 'backmerges';

const cherryPickCardId = (item: QueueItem) => `cherry-pick-queue-${item.id}`;
const backmergeCardId = (pr: PullRequest) => `backmerge-queue-${pr.number}`;

const cherryPickMatches = (item: QueueItem, search: string) =>
    matchesSearch(
        search,
        item.ticket?.identifier,
        item.ticket?.description,
        item.code.identifier,
        codeTitle(item.code),
    );

const backmergeMatches = (pr: PullRequest, search: string) => matchesSearch(search, pr.number, pr.title, pr.baseBranch);

type QueueContentProps = Omit<CherryPickQueueDrawerProps, 'open' | 'onClose'>;

const QueueContent: React.FC<QueueContentProps> = (props) => {
    const queueItems = useSelector(
        (state: RootState) => selectCherryPickQueueItems(state, props.stepRunId),
        shallowEqual,
    );
    const items = React.useMemo(() => [...queueItems].sort(byQueuePosition), [queueItems]);
    const backmergePRs = props.openBackmergePRs ?? [];
    const [search, setSearch] = React.useState('');
    const [activeTab, setActiveTab] = React.useState<QueueTab>(
        isOpenBackmergePR(props.highlightedItem?.code, props.openBackmergePRs) ? 'backmerges' : 'cherryPicks',
    );
    const normalizedSearch = search.trim().toLowerCase();

    const isCherryPickHighlighted = (item: QueueItem) =>
        normalizedSearch ? cherryPickMatches(item, normalizedSearch) : item.id === props.highlightedItem?.id;
    const isBackmergeHighlighted = (pr: PullRequest) =>
        normalizedSearch ?
            backmergeMatches(pr, normalizedSearch)
        :   isOpenBackmergePR(props.highlightedItem?.code, [pr]);

    const firstCherryPick = items.find(isCherryPickHighlighted);
    const firstBackmerge = backmergePRs.find(isBackmergeHighlighted);
    const scrollTargetId =
        activeTab === 'cherryPicks' ?
            firstCherryPick && cherryPickCardId(firstCherryPick)
        :   firstBackmerge && backmergeCardId(firstBackmerge);

    React.useEffect(() => {
        if (scrollTargetId) {
            document.getElementById(scrollTargetId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [scrollTargetId, normalizedSearch]);

    const onSearch = (value: string) => {
        setSearch(value);
        const nextSearch = value.trim().toLowerCase();
        if (!nextSearch) {
            return;
        }
        const hasCherryPickMatch = items.some((item) => cherryPickMatches(item, nextSearch));
        const hasBackmergeMatch = backmergePRs.some((pr) => backmergeMatches(pr, nextSearch));
        if (activeTab === 'cherryPicks' && !hasCherryPickMatch && hasBackmergeMatch) {
            setActiveTab('backmerges');
        } else if (activeTab === 'backmerges' && !hasBackmergeMatch && hasCherryPickMatch) {
            setActiveTab('cherryPicks');
        }
    };

    return (
        <>
            <Input.Search
                allowClear
                placeholder="Search by title, ticket, PR number or branch"
                style={{ marginBottom: 8 }}
                onSearch={onSearch}
            />
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as QueueTab)}
                items={[
                    {
                        key: 'cherryPicks',
                        label: `Cherry-picks (${items.length})`,
                        children: (
                            <List
                                dataSource={items}
                                locale={{ emptyText: 'No fixes waiting to be cherry-picked' }}
                                renderItem={(item) => (
                                    <div
                                        id={cherryPickCardId(item)}
                                        style={queueCardStyle(isCherryPickHighlighted(item))}
                                    >
                                        <CodeStatus
                                            code={item.code}
                                            appId={props.appId}
                                            ignored={item.ignored}
                                            workingBranchEnabled={false}
                                            isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                                        />
                                        <div style={{ marginTop: 8 }}>
                                            <Text strong>{item.ticket?.description ?? codeTitle(item.code)}</Text>
                                        </div>
                                        {item.automationTooltipMessage && (
                                            <Text type="secondary">{item.automationTooltipMessage}</Text>
                                        )}
                                    </div>
                                )}
                            />
                        ),
                    },
                    {
                        key: 'backmerges',
                        label: `Backmerges (${backmergePRs.length})`,
                        children: (
                            <List
                                dataSource={backmergePRs}
                                locale={{ emptyText: 'No open backmerge PRs' }}
                                renderItem={(pr) => (
                                    <div id={backmergeCardId(pr)} style={queueCardStyle(isBackmergeHighlighted(pr))}>
                                        <Tag
                                            color={tagColorForFeatureItemCodeStatus(FeatureItemCodeStatus.OpenPR)}
                                            style={{ ...tagStyle, fontSize: 10 }}
                                        >
                                            Backmerge PR open
                                        </Tag>
                                        <Flex align="center" gap="small" style={{ marginTop: 10 }}>
                                            <Link href={pr.url} target="_blank" strong>
                                                #{pr.number}
                                            </Link>
                                            <Divider
                                                type="vertical"
                                                style={{
                                                    backgroundColor: antGrey.grey5,
                                                    marginLeft: 2,
                                                    marginRight: 2,
                                                    height: 14,
                                                }}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <PullRequestOutlined />
                                                <Text style={{ fontFamily: 'monospace', fontSize: 12, marginLeft: 8 }}>
                                                    {pr.baseBranch}
                                                </Text>
                                            </div>
                                        </Flex>
                                        <div style={{ marginTop: 8 }}>
                                            <Text strong>{pr.title}</Text>
                                        </div>
                                        <Text type="secondary">
                                            {displayTextForPullRequestType(pr.pullRequestType)}
                                        </Text>
                                    </div>
                                )}
                            />
                        ),
                    },
                ]}
            />
        </>
    );
};

const CherryPickQueueDrawer: React.FC<CherryPickQueueDrawerProps> = ({ open, onClose, ...contentProps }) => (
    <Drawer open={open} onClose={onClose} width={680} title="Cherry-pick and backmerge queue" destroyOnClose>
        <QueueContent {...contentProps} />
    </Drawer>
);

export default CherryPickQueueDrawer;
